import { Plus, Trash2 } from 'lucide-react';
import ImageField from './ImageField';

const isImageField = (path, key) => key === 'image' || path === 'siteImages' || path.startsWith('siteImages-');

const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
const labelise = (k) => k.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

function blankLike(sample) {
  if (Array.isArray(sample)) return sample.map(blankLike);
  if (isObj(sample)) {
    const out = {};
    Object.keys(sample).forEach((k) => { out[k] = blankLike(sample[k]); });
    return out;
  }
  return '';
}

function Leaf({ value, onChange, testid }) {
  const text = value == null ? '' : String(value);
  if (text.length > 70) {
    return (
      <textarea value={text} rows={Math.min(8, Math.ceil(text.length / 78) + 1)} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 rounded-xl bg-white border border-[var(--mm-line)] text-sm text-[var(--mm-ink)] resize-y" />
    );
  }
  return (
    <input value={text} data-testid={testid} onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 px-3 rounded-xl bg-white border border-[var(--mm-line)] text-sm text-[var(--mm-ink)]" />
  );
}

function StringList({ items, onChange, testid }) {
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex gap-2 items-start">
          <div className="flex-1 min-w-0">
            <Leaf value={item} testid={`${testid}-${i}`} onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))} />
          </div>
          <button type="button" data-testid={`remove-${testid}-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))}
            className="shrink-0 w-9 h-9 rounded-full border border-[var(--mm-line)] bg-white flex items-center justify-center text-red-500 hover:border-red-300">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <AddBtn testid={testid} onClick={() => onChange([...items, ''])} />
    </div>
  );
}

function AddBtn({ onClick, testid, label = 'Add item' }) {
  return (
    <button type="button" onClick={onClick} data-testid={`add-${testid}`}
      className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--mm-purple)] hover:opacity-70">
      <Plus className="w-4 h-4" /> {label}
    </button>
  );
}

// One record: an object whose values are strings or string arrays.
function RecordFields({ record, onChange, testid }) {
  return (
    <div className="space-y-4">
      {Object.keys(record).map((k) => {
        const v = record[k];
        const set = (nv) => onChange({ ...record, [k]: nv });
        return (
          <div key={k}>
            <label className="block text-xs font-semibold tracking-wider text-[var(--mm-ink)]/60 uppercase mb-2">{labelise(k)}</label>
            {Array.isArray(v) ? (
              <StringList items={v} onChange={set} testid={`${testid}-${k}`} />
            ) : isImageField(testid, k) ? (
              <ImageField value={v} onChange={set} testid={`${testid}-${k}`} />
            ) : isObj(v) ? (
              <div className="rounded-xl border border-[var(--mm-line)] p-3 space-y-3">
                {Object.keys(v).map((sk) => (
                  <div key={sk}>
                    <label className="block text-[11px] font-semibold tracking-wider text-[var(--mm-ink)]/50 uppercase mb-1.5">{labelise(sk)}</label>
                    <Leaf value={v[sk]} testid={`${testid}-${k}-${sk}`} onChange={(nv) => set({ ...v, [sk]: nv })} />
                  </div>
                ))}
              </div>
            ) : (
              <Leaf value={v} testid={`${testid}-${k}`} onChange={set} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ItemCard({ index, onRemove, testid, children }) {
  return (
    <div className="rounded-2xl border border-[var(--mm-line)] bg-[var(--mm-cream)] p-4 md:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="mm-kicker">Item {index + 1}</div>
        <button type="button" onClick={onRemove} data-testid={`remove-${testid}-${index}`}
          className="shrink-0 w-9 h-9 rounded-full border border-[var(--mm-line)] bg-white flex items-center justify-center text-red-500 hover:border-red-300">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {children}
    </div>
  );
}

export default function ValueEditor({ value, onChange, path = 'section' }) {
  if (isObj(value)) return <RecordFields record={value} onChange={onChange} testid={path} />;

  if (Array.isArray(value)) {
    if (value.every((v) => typeof v === 'string')) {
      return <StringList items={value} onChange={onChange} testid={path} />;
    }
    const update = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
    const remove = (i) => onChange(value.filter((_, j) => j !== i));
    const add = () => onChange([...value, blankLike(value[0] ?? '')]);

    return (
      <div className="space-y-3">
        {value.map((item, i) => (
          <ItemCard key={i} index={i} testid={path} onRemove={() => remove(i)}>
            {Array.isArray(item) ? (
              <StringList items={item} onChange={(v) => update(i, v)} testid={`${path}-${i}`} />
            ) : isObj(item) ? (
              <RecordFields record={item} onChange={(v) => update(i, v)} testid={`${path}-${i}`} />
            ) : (
              <Leaf value={item} testid={`${path}-${i}`} onChange={(v) => update(i, v)} />
            )}
          </ItemCard>
        ))}
        <AddBtn testid={path} onClick={add} />
      </div>
    );
  }

  return <Leaf value={value} onChange={onChange} testid={path} />;
}
